import { type WebhookType } from '@repo/crm-atoms';
import { message } from '@repo/ui';
import { type NodeProps } from '@xyflow/react';
import { Drawer } from 'antd';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type WebhookNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { saveActionNode } from 'automations/utils/actionNode';
import { ModifyWebhookDrawer } from 'crm-atoms/components';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export default function WebhookActionNodeContent({
  node,
}: {
  node: NodeProps<WebhookNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const { open, setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const webhookId = node.data?.additionalOptions?.webhookId;
  const parentId = useSelector(getParentIdSelector(node.id));

  const handleFinish = useCallback(async (webhook: WebhookType) => {
    try {
      if (webhookId && webhook.id === webhookId) {
        message.success('Webhook Updated');
        return;
      }

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: {
          webhookId: webhook.id,
        },
      }));
      await dispatch(saveActionNode(node.id, parentId, contentMode));
    } catch (error: any) {
      message.error(error?.data?.message ?? 'Something Went Wrong Updating Node');
    } finally {
      setMenuOpen(false);
    }
  }, [contentMode, dispatch, node.id, parentId, setMenuOpen, webhookId]);

  return (
    <Drawer
      title={contentMode === 'create' ? 'Create Webhook' : 'Edit Webhook'}
      open={open}
      onClose={() => setMenuOpen(false)}
      size="large"
      placement="bottom"
      destroyOnClose
    >
      <ModifyWebhookDrawer
        mode={contentMode}
        onFinish={handleFinish}
        onClose={() => setMenuOpen(false)}
        webhookId={webhookId}
      />
    </Drawer>
  );
}
